import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, ShieldCheck, Headphones, Wrench, MapPin, Check, X, BookOpen } from 'lucide-react';
import GoogleRating from '@/components/cinematic/GoogleRating';

/**
 * "למה לשאול דווקא את בניהו · בנה מחשבים" — the trust/positioning block that
 * justifies a premium over KSP/Amazon for a small, high-service northern shop.
 * Founder presence + value pillars + honest us-vs-big-retailers table + consult CTA.
 *
 * The founder photo auto-loads from /bnayahu.jpg (drop the real photo in
 * public/bnayahu.jpg). Until then it falls back to a branded initial avatar.
 *
 * variant="section" → full width section (home / shop / between content)
 * variant="card"    → compact card for a product-page sidebar
 */
const WHATSAPP_CONSULT =
  'https://wa.me/972546672863?text=' +
  encodeURIComponent('שלום בניהו, יש לי שאלה לפני קנייה — אשמח לייעוץ קצר.');

const PILLARS = [
  { icon: MessageCircle, title: 'ייעוץ בלי עלות ובלי התחייבות', text: 'מתלבטים בין דגמים? תשאלו לפני שאתם מזמינים, לא משנה ממי. אני אגיד מה באמת מתאים, גם כשזה הזול יותר וגם כשזה "אל תקנו עכשיו".' },
  { icon: ShieldCheck, title: 'מזמינים דרכי רק אם זה מתאים', text: 'שולחים רשימה בוואטסאפ, מקבלים הצעת מחיר, ומקבלים עם UPS לכל הארץ (חינם בהזמנה מעל ₪2,000) או אוספים בנטור. מחשב מגיע מותקן ומוגדר, מוכן לשימוש; אחריות יצרן, והטיפול באחריות דרך המעבדה בנטור. העצה שלי זהה גם אם תחליטו לקנות במקום אחר.' },
  { icon: Headphones, title: 'מעבדה אמיתית מאחורי העצה', text: 'מי שעונה לכם הוא מי שמפרק את המחשבים האלה בפועל, בגולן, בעברית. לא מוקד בחו״ל.' },
  { icon: Wrench, title: 'ידע פתוח, לא נעול', text: 'המדריכים והכלים כאן חינמיים במלואם. מי שרוצה לעשות את זה לבד, שיעשה לבד.' },
];

const COMPARE = [
  'מענה אנושי אישי (לא בוט/מוקד)',
  'ייעוץ מטכנאי אמיתי, בלי עלות',
  'אחריות ותמיכה מקומית בעברית',
  'מעבדה פיזית שאפשר להגיע אליה',
  'ליווי והגדרה אחרי הקנייה',
];

export function FounderAvatar({ size = 72, dot = true }: { size?: number; dot?: boolean }) {
  const [broken, setBroken] = useState(false);
  const inner = broken ? (
    <div
      className="flex h-full w-full items-center justify-center rounded-full font-black text-white"
      style={{ fontSize: size * 0.4, background: 'linear-gradient(135deg, var(--cin-blue) 0%, var(--cin-teal) 100%)' }}
      aria-hidden="true"
    >ב</div>
  ) : (
    <img
      src="/bnayahu.jpg"
      alt="בניהו — בעלים, בנה מחשבים"
      onError={() => setBroken(true)}
      className="h-full w-full rounded-full object-cover"
      decoding="async"
      fetchPriority="high"
    />
  );
  return (
    <div
      className="relative flex-shrink-0 rounded-full shadow-xl"
      style={{
        width: size, height: size, padding: Math.max(3, size * 0.045),
        background: 'linear-gradient(135deg, var(--cin-blue) 0%, var(--cin-teal) 55%, var(--cin-amber) 100%)',
      }}
    >
      <div className="h-full w-full rounded-full bg-white" style={{ padding: Math.max(2, size * 0.03) }}>
        <div className="h-full w-full overflow-hidden rounded-full">{inner}</div>
      </div>
      {dot && (
        <span
          className="absolute bottom-0.5 left-0.5 rounded-full border-2 border-white bg-[var(--cin-teal)]"
          style={{ width: size * 0.22, height: size * 0.22 }}
          title="מענה אישי"
          aria-hidden="true"
        />
      )}
    </div>
  );
}

export default function WhyBuyFromUs({ variant = 'section' }: { variant?: 'section' | 'card' }) {
  const Inner = (
    <>
      {/* Founder presence */}
      <div className="flex items-center gap-4 mb-6">
        <FounderAvatar size={variant === 'card' ? 56 : 72} />
        <div>
          <div className="text-lg md:text-xl font-black text-[var(--cin-ink)]">בניהו · בנה מחשבים</div>
          <div className="text-sm text-[var(--cin-muted)] leading-snug mb-1.5">
            אדם אחד עם מעבדה בגולן, שעומד מאחורי כל הזמנה. לא תאגיד, לא מוקד.
          </div>
          <GoogleRating />
        </div>
      </div>

      {/* Value pillars */}
      <div className={`grid gap-3 ${variant === 'card' ? 'grid-cols-1' : 'sm:grid-cols-2'}`}>
        {PILLARS.map(({ icon: Icon, title, text }) => (
          <div key={title} className="flex items-start gap-3 rounded-xl border border-[var(--cin-line)] bg-white p-3.5">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[var(--cin-blue)]/10 text-[var(--cin-blue)]">
              <Icon className="h-4.5 w-4.5" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-bold text-[var(--cin-ink)] leading-tight">{title}</div>
              <div className="text-[12px] text-[var(--cin-muted)] leading-snug mt-0.5">{text}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Us vs big retailers */}
      {variant === 'section' && (
        <div className="mt-6 rounded-xl border border-[var(--cin-line)] overflow-hidden">
          <div className="grid grid-cols-[1fr_auto_auto] items-center bg-[var(--cin-paper-soft)] text-xs font-bold text-[var(--cin-ink)]">
            <div className="p-3">מה שחשוב באמת</div>
            <div className="p-3 text-center text-[var(--cin-blue)] w-24">בנה מחשבים</div>
            <div className="p-3 text-center text-[var(--cin-muted)] w-24">אתרים גדולים</div>
          </div>
          {COMPARE.map((row, i) => (
            <div key={row} className={`grid grid-cols-[1fr_auto_auto] items-center text-sm ${i % 2 ? 'bg-white' : 'bg-[var(--cin-paper-soft)]/40'}`}>
              <div className="p-3 text-[var(--cin-ink)]">{row}</div>
              <div className="p-3 text-center w-24"><Check className="mx-auto h-4 w-4 text-[var(--cin-teal)]" /></div>
              <div className="p-3 text-center w-24"><X className="mx-auto h-4 w-4 text-[var(--cin-line)]" /></div>
            </div>
          ))}
        </div>
      )}

      {/* CTA */}
      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <a href={WHATSAPP_CONSULT} target="_blank" rel="noopener noreferrer" className="cin-btn-primary cin-magnetic justify-center">
          <MessageCircle className="h-5 w-5" />
          יש שאלה לפני שקונים? כתבו לבניהו
        </a>
        <Link to="/laptop-guide-2026/" className="cin-btn-ghost inline-flex items-center justify-center gap-2">
          <BookOpen className="h-5 w-5" />
          המדריך המלא לבחירת מחשב
        </Link>
      </div>
      <div className="mt-3 flex items-center gap-1.5 text-xs text-[var(--cin-muted)]">
        <MapPin className="h-3.5 w-3.5" />
        מעבדה בנטור, דרום רמת הגולן · בוואטסאפ עונה בניהו עצמו, לא מוקד
      </div>
    </>
  );

  if (variant === 'card') {
    return (
      <div className="rounded-2xl border border-[var(--cin-line)] bg-white p-5">{Inner}</div>
    );
  }

  return (
    <section className="bg-[var(--cin-paper-soft)] py-[clamp(48px,8vw,110px)] px-[clamp(20px,5vw,80px)] border-y border-[var(--cin-line)]">
      <div className="mx-auto max-w-[1080px]">
        <div className="cin-reveal cin-label-ed mb-3 text-[var(--cin-blue)]">למה דווקא אנחנו</div>
        <h2 className="cin-reveal cin-reveal-d1 cin-display-sm mb-3 text-[var(--cin-ink)]">
          למה לשאול דווקא <span className="cin-text-gradient-blue">את בניהו.</span>
        </h2>
        <p className="cin-reveal cin-reveal-d2 text-base md:text-lg text-[var(--cin-muted)] max-w-2xl leading-relaxed mb-8">
          המדריכים, הכלים והייעוץ בחינם. אני מרוויח כשמביאים אליי מחשב לתיקון
          או מזמינים דרך המעבדה, ולכן העצה צריכה להיות כזו שתרצו לחזור אליה.
        </p>
        <div className="cin-reveal cin-reveal-d3">{Inner}</div>
      </div>
    </section>
  );
}
